// フォルダツリーの階層計算・パンくず・開閉状態
import { computed, nextTick, ref, watch } from 'vue';

const STORAGE_KEY = 'exa_dashboard_collapsed_folders';

const isInternalDashboardNavigation = () => {
  // F5 再読み込みは referrer が保持されるため、navigation type で判定する
  try {
    if (performance.getEntriesByType('navigation')[0]?.type === 'reload') return false;
  } catch (e) {
    // API が無い・例外のときは referrer 判定へ進む
  }
  const referrer = document.referrer;
  if (!referrer) return false;
  try {
    const referrerUrl = new URL(referrer);
    return referrerUrl.origin === window.location.origin && referrerUrl.pathname === '/dashboard';
  } catch (e) {
    return false;
  }
};

// restored: localStorage から開閉状態を復元したか (false のときは呼び出し側が全折りたたみを適用する)
const loadInitialCollapsed = () => {
  if (!isInternalDashboardNavigation()) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore storage errors
    }
    return { set: new Set(), restored: false };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { set: new Set(), restored: true };
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return { set: new Set(), restored: true };
    return { set: new Set(arr.map(String)), restored: true };
  } catch (e) {
    return { set: new Set(), restored: true };
  }
};

// トップレベルのみ展開・子孫は全て折りたたむ状態の Set を算出する（collapseAllFolders と初期折りたたみ初期化の共通ロジック）
const computeCollapseAllSet = (list) => {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((folder) => {
    const key = String(folder.parent_id ?? 0);
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(folder);
  });

  const next = new Set();
  Object.keys(map).forEach((parentId) => {
    if (parentId === '0') {
      return;
    }
    if ((map[parentId] || []).length > 0) {
      next.add(String(parentId));
    }
  });

  return next;
};

export const useFolderTree = (folders, selectedFolderId, options = {}) => {
  const persist = options.persist !== false;
  const initialCollapse = options.initialCollapse || 'restore';
  const shouldInitCollapseAll = !persist && initialCollapse === 'collapseAll';
  const autoRevealSelection = options.autoRevealSelection === true;
  const beforeScroll = typeof options.beforeScroll === 'function' ? options.beforeScroll : null;
  const scrollContainerSelector = options.scrollContainerSelector || '.folder-tree-list';

  const initialLoad = persist ? loadInitialCollapsed() : { set: new Set(), restored: false };
  const collapsedFolders = ref(initialLoad.set);
  // persist=true で保存状態を復元しなかったとき (他ページからの遷移・F5・直接アクセス) は、最初の一覧取得時に全折りたたみで始める
  const shouldInitCollapseAllOnLoad = persist && !initialLoad.restored;

  if (persist) {
    watch(collapsedFolders, (set) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
      } catch (e) {
        // ignore storage errors
      }
    });
  }

  let collapseAllApplied = false;
  let collapseAllOnLoadApplied = false;

  const childrenByParent = computed(() => {
    const map = {};
    (folders.value || []).forEach((folder) => {
      const key = String(folder.parent_id ?? 0);
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(folder);
    });
    return map;
  });

  const topLevelFolders = computed(() => childrenByParent.value['0'] || []);

  const getChildren = (parentId) => childrenByParent.value[String(parentId)] || [];

  const folderMap = computed(() => {
    const map = {};
    (folders.value || []).forEach((folder) => {
      map[String(folder.id)] = folder;
    });
    return map;
  });

  const getAncestorFolderIds = (folderId) => {
    const map = folderMap.value;
    const ancestors = [];
    let current = map[String(folderId)];
    if (!current) return ancestors;
    let pid = current.parent_id ?? 0;
    while (pid) {
      ancestors.push(String(pid));
      current = map[String(pid)];
      if (!current) break;
      pid = current.parent_id ?? 0;
    }
    return ancestors;
  };

  const expandAncestorsOf = (folderId) => {
    const ancestors = getAncestorFolderIds(folderId);
    if (!ancestors.length) return;
    const next = new Set(collapsedFolders.value);
    let dirty = false;
    ancestors.forEach((id) => {
      if (next.delete(id)) {
        dirty = true;
      }
    });
    if (dirty) {
      collapsedFolders.value = next;
    }
  };

  // expandAncestorsOf / folderMap の定義後に置く (immediate: true で即時実行されるため、定義前だと未初期化参照になる)
  watch(
    folders,
    (list) => {
      const arr = Array.isArray(list) ? list : [];
      if (!arr.length) return;

      if (shouldInitCollapseAll && !collapseAllApplied) {
        collapseAllApplied = true;
        collapsedFolders.value = computeCollapseAllSet(arr);
        return;
      }

      if (shouldInitCollapseAllOnLoad && !collapseAllOnLoadApplied) {
        collapseAllOnLoadApplied = true;
        collapsedFolders.value = computeCollapseAllSet(arr);
        // watcher の実行順に依存させず、選択中フォルダの祖先は全折りたたみの直後に同期的に開く
        if (selectedFolderId.value) {
          expandAncestorsOf(selectedFolderId.value);
        }
        return;
      }

      const validIds = new Set(arr.map((f) => String(f.id)));
      const current = collapsedFolders.value;
      if (current.size === 0) return;
      const next = new Set();
      let dirty = false;
      current.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          dirty = true;
        }
      });
      if (dirty) {
        collapsedFolders.value = next;
      }
    },
    { immediate: true }
  );

  const scrollSelectedFolderIntoView = async (folderId) => {
    if (!folderId) return;
    await nextTick();
    if (beforeScroll) {
      beforeScroll(); // スクロール位置計算前にレイアウト（restore-bar高さのCSS変数）を同期確定させる
    }
    const selector = `${scrollContainerSelector} .folder-link[data-folder-id="${folderId}"]`;
    const targetElement = document.querySelector(selector);
    if (!targetElement || typeof targetElement.scrollIntoView !== 'function') {
      return;
    }
    targetElement.getBoundingClientRect(); // Vueマウント直後のレイアウトを強制確定させてからsmoothスクロールする（直リンク時のスクロール不足対策）
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };

  if (autoRevealSelection) {
    watch(selectedFolderId, (folderId) => {
      if (!folderId) return;
      expandAncestorsOf(folderId);
      scrollSelectedFolderIntoView(folderId);
    });
  }

  const folderBreadcrumbItems = computed(() => {
    const map = folderMap.value;
    const path = [];
    let current = map[String(selectedFolderId.value)];
    while (current) {
      path.unshift({
        id: current.id,
        name: current.name,
      });
      const pid = current.parent_id ?? 0;
      if (!pid) break;
      current = map[String(pid)];
    }
    return path.length ? path : [];
  });

  const folderBreadcrumb = computed(() =>
    folderBreadcrumbItems.value.map((item) => item.name)
  );

  const isCollapsed = (folderId) => collapsedFolders.value.has(String(folderId));

  const toggleFolder = (folderId) => {
    const key = String(folderId);
    const next = new Set(collapsedFolders.value);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    collapsedFolders.value = next;
  };

  const expandAllFolders = () => {
    collapsedFolders.value = new Set();
  };

  const collapseAllFolders = () => {
    collapsedFolders.value = computeCollapseAllSet(folders.value);
  };

  // 子を持つフォルダ (TOP以外) が全て折りたたみ済みなら true
  const isAllFoldersCollapsed = computed(() => {
    const target = computeCollapseAllSet(folders.value);
    if (target.size === 0) return false;
    const current = collapsedFolders.value;
    for (const id of target) {
      if (!current.has(id)) return false;
    }
    return true;
  });

  const toggleAllFolders = () => {
    if (isAllFoldersCollapsed.value) {
      expandAllFolders();
    } else {
      collapseAllFolders();
    }
  };

  const getFolderPath = (folderId) => {
    if (!folderId) return '-';
    const map = folderMap.value;
    const path = [];
    let current = map[String(folderId)];
    while (current) {
      path.unshift(current.name);
      const pid = current.parent_id ?? 0;
      if (!pid) break;
      current = map[String(pid)];
    }
    return path.length ? path.join(' / ') : '-';
  };

  // ルート→末端順。folderMapで解決できない場合は空配列（呼び出し側でフォールバック）
  const getFolderPathItems = (folderId) => {
    if (!folderId) return [];
    const map = folderMap.value;
    const path = [];
    let current = map[String(folderId)];
    while (current) {
      path.unshift({ id: current.id, name: current.name });
      const pid = current.parent_id ?? 0;
      if (!pid) break;
      current = map[String(pid)];
    }
    return path;
  };

  return {
    topLevelFolders,
    getChildren,
    folderBreadcrumbItems,
    folderBreadcrumb,
    isCollapsed,
    toggleFolder,
    expandAllFolders,
    collapseAllFolders,
    isAllFoldersCollapsed,
    toggleAllFolders,
    getFolderPath,
    getFolderPathItems,
    expandAncestorsOf,
    scrollSelectedFolderIntoView,
  };
};
